import { __assign } from "tslib";
import color from 'color';
import { lighten, alpha, generateColorScales, defaultGrayShadesPercentages, redesignScales } from '../utils/colors';
import { createShadows, createDarkShadows } from './shadows';
/**
 * normalize open-color to remove some colours,
 * such as 'grape'. This should be compatible with a tool
 * like palx.
 */

var defaultScales = {
  green: generateColorScales('#17CF77'),
  blue: generateColorScales('#4B4BFF'),
  yellow: generateColorScales('#E9D453'),
  red: generateColorScales('#FF5A31'),
  cyan: generateColorScales('#32C8FF'),
  violet: generateColorScales('#A050C8'),
  pink: generateColorScales('#EBA2B1'),
  gray: generateColorScales('#BBBCBC'),
  grayShades: generateColorScales('#021215', 12, //Scale length 
  defaultGrayShadesPercentages, 'grayScale')
};
var redesignColorScales = {
  green: redesignScales.green,
  blue: redesignScales.blue,
  lightBlue: redesignScales.lightBlue,
  purple: redesignScales.purple,
  red: redesignScales.red,
  pink: redesignScales.pink,
  yellow: redesignScales.yellow,
  gray: redesignScales.gray,
  grayShades: generateColorScales('#021215', 12, //Scale length 
  defaultGrayShadesPercentages, 'grayScale')
};

var getFullColorsObject = function getFullColorsObject(colors, type) {
  var fullObject = {};
  colors.forEach(function (color, i) {
    Object.defineProperty(fullObject, "".concat(type).concat(i > 0 ? i : '', "00"), {
      value: color,
      enumerable: true
    });
  });
  return fullObject;
};

var getFullShadesObject = function getFullShadesObject(shades) {
  var fullObject = {};
  shades.pop();
  shades.forEach(function (shade, i) {
    Object.defineProperty(fullObject, "".concat(i > 0 ? 'black' : 'white').concat(i > 0 && i !== shades.length - 1 ? i + '00' : ''), {
      value: shade,
      enumerable: true
    });
  });
  return fullObject;
};

function redesignGeneratePalette(scales) {
  return {
    primary: {
      lightest: scales.green[1],
      light: scales.green[4],
      base: scales.green[5],
      dark: scales.green[6],
      fullScale: scales.green,
      fullObject: getFullColorsObject(scales.green, 'green')
    },
    secondary: {
      lightest: scales.blue[1],
      light: scales.blue[4],
      base: scales.blue[5],
      dark: scales.blue[6],
      fullScale: scales.blue,
      fullObject: getFullColorsObject(scales.blue, 'blue')
    },
    green: {
      lightest: scales.green[1],
      light: scales.green[4],
      base: scales.green[5],
      dark: scales.green[6],
      fullScale: scales.green,
      fullObject: getFullColorsObject(scales.green, 'green')
    },
    blue: {
      lightest: scales.blue[1],
      light: scales.blue[4],
      base: scales.blue[5],
      dark: scales.blue[6],
      fullScale: scales.blue,
      fullObject: getFullColorsObject(scales.blue, 'blue')
    },
    yellow: {
      lightest: scales.yellow[1],
      light: scales.yellow[4],
      base: scales.yellow[5],
      dark: scales.yellow[6],
      fullScale: scales.yellow,
      fullObject: getFullColorsObject(scales.yellow, 'yellow')
    },
    red: {
      lightest: scales.red[1],
      light: scales.red[4],
      base: scales.red[5],
      dark: scales.red[6],
      fullScale: scales.red,
      fullObject: getFullColorsObject(scales.red, 'red')
    },
    lightBlue: {
      lightest: scales.lightBlue[1],
      light: scales.lightBlue[4],
      base: scales.lightBlue[5],
      dark: scales.lightBlue[6],
      fullScale: scales.lightBlue,
      fullObject: getFullColorsObject(scales.lightBlue, 'lightBlue')
    },
    purple: {
      lightest: scales.purple[1],
      light: scales.purple[4],
      base: scales.purple[5],
      dark: scales.purple[6],
      fullScale: scales.purple,
      fullObject: getFullColorsObject(scales.purple, 'purple')
    },
    pink: {
      lightest: scales.pink[1],
      light: scales.pink[4],
      base: scales.pink[5],
      dark: scales.pink[6],
      fullScale: scales.pink,
      fullObject: getFullColorsObject(scales.pink, 'pink')
    },
    gray: {
      lightest: scales.gray[1],
      light: scales.gray[4],
      base: scales.gray[5],
      dark: scales.gray[6],
      fullScale: scales.gray,
      fullObject: getFullColorsObject(scales.gray, 'gray')
    },
    grayShades: {
      lightest: scales.grayShades[1],
      light: scales.grayShades[9],
      base: scales.grayShades[10],
      dark: scales.grayShades[11],
      fullScale: scales.grayShades,
      fullObject: getFullShadesObject(scales.grayShades)
    }
  };
}
/**
 * Generate lightmode colors
 * @param scales
 * @param palette
 */


function defaultGenerateLightMode(scales, palette) {
  return {
    background: {
      tint1: scales.gray[1],
      tint2: scales.gray[3],
      overlay: alpha(scales.grayShades[11], 0.8),
      layer: 'white',
      default: 'white'
    },
    border: {
      default: scales.grayShades[3],
      muted: alpha(scales.gray[9], 0.08)
    },
    text: {
      heading: scales.grayShades[11],
      muted: color(scales.gray[7]).lighten(0.3).hex().toString(),
      default: scales.gray[9],
      selected: palette.blue.base,
      intent: 'white'
    },
    shadows: createShadows(scales.gray[8])
  };
}
/**
 * Generate dark mode colors
 * @param scales
 * @param palette
 */


function defaultGenerateDarkMode(scales, palette) {
  var base = palette.grayShades.light;
  return {
    background: {
      tint1: lighten(base, 0.5),
      tint2: lighten(base, 0.7),
      overlay: alpha(scales.grayShades[11], 0.8),
      layer: lighten(base, 0.2),
      default: base
    },
    border: {
      default: scales.grayShades[7],
      muted: alpha(scales.gray[0], 0.08)
    },
    text: {
      heading: 'white',
      muted: 'rgba(255,255,255,0.7)',
      default: 'rgba(255,255,255,0.88)',
      selected: palette.blue.base,
      intent: 'black'
    },
    shadows: createDarkShadows('black')
  };
}

function overridePaletteItemBaseColor(paletteItem, color) {
  return __assign(__assign({}, paletteItem), {
    base: color
  });
}

function defaultGenerateLightSemanticColors(palette) {
  return {
    positive: palette.green.fullScale[7],
    warning: '#FFC043',
    negative: '#D10000',
    feedback: palette.blue.base,
    disabled: palette.grayShades.fullScale[4]
  };
}

function defaultGenerateDarkSemanticColors(palette) {
  return {
    positive: palette.green.fullScale[7],
    warning: '#FFC043',
    negative: '#D10000',
    feedback: palette.blue.base,
    disabled: palette.grayShades.fullScale[8]
  };
}

function defaultGenerateLightLinkColors(palette) {
  var _a, _b, _c, _d, _e;

  return {
    default: (_a = palette.blue.fullObject) === null || _a === void 0 ? void 0 : _a.blue700,
    hover: (_b = palette.blue.fullObject) === null || _b === void 0 ? void 0 : _b.blue500,
    active: (_c = palette.blue.fullObject) === null || _c === void 0 ? void 0 : _c.blue900,
    focus: (_d = palette.blue.fullObject) === null || _d === void 0 ? void 0 : _d.blue1000,
    visited: (_e = palette.purple.fullObject) === null || _e === void 0 ? void 0 : _e.purple600
  };
}

function defaultGenerateDarkLinkColors(palette) {
  var _a, _b, _c, _d, _e;

  return {
    default: (_a = palette.blue.fullObject) === null || _a === void 0 ? void 0 : _a.blue300,
    hover: (_b = palette.blue.fullObject) === null || _b === void 0 ? void 0 : _b.blue200,
    active: (_c = palette.blue.fullObject) === null || _c === void 0 ? void 0 : _c.blue100,
    focus: (_d = palette.blue.fullObject) === null || _d === void 0 ? void 0 : _d.blue400,
    visited: (_e = palette.purple.fullObject) === null || _e === void 0 ? void 0 : _e.purple300
  };
}

function defaultGenerateLightContentColors(palette) {
  var _a, _b, _c, _d;

  return {
    primary: (_a = palette.grayShades.fullObject) === null || _a === void 0 ? void 0 : _a.black,
    secondary: (_b = palette.grayShades.fullObject) === null || _b === void 0 ? void 0 : _b.black700,
    tertiary: (_c = palette.grayShades.fullObject) === null || _c === void 0 ? void 0 : _c.black500,
    alternative: (_d = palette.grayShades.fullObject) === null || _d === void 0 ? void 0 : _d.white
  };
}

function defaultGenerateDarkContentColors(palette) {
  var _a, _b, _c, _d;

  return {
    primary: (_a = palette.grayShades.fullObject) === null || _a === void 0 ? void 0 : _a.white,
    secondary: (_b = palette.grayShades.fullObject) === null || _b === void 0 ? void 0 : _b.black200,
    tertiary: (_c = palette.grayShades.fullObject) === null || _c === void 0 ? void 0 : _c.black400,
    alternative: (_d = palette.grayShades.fullObject) === null || _d === void 0 ? void 0 : _d.black
  };
}

function defaultGenerateLightBackgroundColors(palette) {
  var _a, _b, _c, _d, _e, _f;

  return {
    home: (_a = palette.blue.fullObject) === null || _a === void 0 ? void 0 : _a.blue00,
    section: (_b = palette.grayShades.fullObject) === null || _b === void 0 ? void 0 : _b.white,
    alternative: (_c = palette.gray.fullObject) === null || _c === void 0 ? void 0 : _c.gray100,
    lightYellow: (_d = palette.yellow.fullObject) === null || _d === void 0 ? void 0 : _d.yellow200,
    blue: palette.blue.base,
    yellow: palette.yellow.base,
    purple: palette.purple.base,
    lightBlue200: (_e = palette.lightBlue.fullObject) === null || _e === void 0 ? void 0 : _e.lightBlue200,
    lightBlue: palette.lightBlue.base,
    pink: palette.pink.base,
    lightBlue1000: (_f = palette.lightBlue.fullObject) === null || _f === void 0 ? void 0 : _f.lightBlue1000,
    overlay: 'rgba(2, 18, 21, 0.5)'
  };
}

function defaultGenerateDarkBackgroundColors(palette) {
  var _a, _b, _c, _d, _e, _f;

  return {
    home: (_a = palette.grayShades.fullObject) === null || _a === void 0 ? void 0 : _a.black1000,
    section: (_b = palette.grayShades.fullObject) === null || _b === void 0 ? void 0 : _b.black900,
    alternative: (_c = palette.grayShades.fullObject) === null || _c === void 0 ? void 0 : _c.black700,
    lightYellow: (_d = palette.yellow.fullObject) === null || _d === void 0 ? void 0 : _d.yellow200,
    blue: palette.blue.base,
    yellow: palette.yellow.base,
    purple: palette.purple.base,
    lightBlue200: (_e = palette.lightBlue.fullObject) === null || _e === void 0 ? void 0 : _e.lightBlue200,
    lightBlue: palette.lightBlue.base,
    pink: palette.pink.base,
    lightBlue1000: (_f = palette.lightBlue.fullObject) === null || _f === void 0 ? void 0 : _f.lightBlue1000,
    overlay: 'rgba(2, 18, 21, 0.5)'
  };
}

function defaultGenerateLightIntents(palette) {
  return {
    none: palette.gray,
    primary: palette.green,
    success: overridePaletteItemBaseColor(palette.green, '#1C8537'),
    info: overridePaletteItemBaseColor(palette.blue, '#5666F5'),
    danger: overridePaletteItemBaseColor(palette.red, '#D6411C'),
    warning: overridePaletteItemBaseColor(palette.yellow, '#897414')
  };
}

function defaultGenerateDarkIntents(palette) {
  return {
    none: palette.gray,
    primary: palette.green,
    success: overridePaletteItemBaseColor(palette.green, '#3AA355'),
    info: overridePaletteItemBaseColor(palette.blue, '#7989FF'),
    danger: overridePaletteItemBaseColor(palette.red, '#F45F3A'),
    warning: overridePaletteItemBaseColor(palette.yellow, '#F5E058')
  };
}

export function generateColorsFromScales(scales, generators) {
  if (generators === void 0) {
    generators = {};
  }

  var _a = __assign({
    generateLightIntents: defaultGenerateLightIntents,
    generateDarkIntents: defaultGenerateDarkIntents,
    generatePalette: redesignGeneratePalette,
    generateLightMode: defaultGenerateLightMode,
    generateDarkMode: defaultGenerateDarkMode,
    generateLightSemanticColors: defaultGenerateLightSemanticColors,
    generateDarkSemanticColors: defaultGenerateDarkSemanticColors,
    generateLightLinkColors: defaultGenerateLightLinkColors,
    generateDarkLinkColors: defaultGenerateDarkLinkColors,
    generateLightContentColors: defaultGenerateLightContentColors,
    generateDarkContentColors: defaultGenerateDarkContentColors,
    generateLightBackgroundColors: defaultGenerateLightBackgroundColors,
    generateDarkBackgroundColors: defaultGenerateDarkBackgroundColors
  }, generators),
      generateLightIntents = _a.generateLightIntents,
      generateDarkIntents = _a.generateDarkIntents,
      generatePalette = _a.generatePalette,
      generateLightMode = _a.generateLightMode,
      generateDarkMode = _a.generateDarkMode,
      generateLightSemanticColors = _a.generateLightSemanticColors,
      generateDarkSemanticColors = _a.generateDarkSemanticColors,
      generateLightLinkColors = _a.generateLightLinkColors,
      generateDarkLinkColors = _a.generateDarkLinkColors,
      generateLightContentColors = _a.generateLightContentColors,
      generateDarkContentColors = _a.generateDarkContentColors,
      generateLightBackgroundColors = _a.generateLightBackgroundColors,
      generateDarkBackgroundColors = _a.generateDarkBackgroundColors;

  var palette = generatePalette(scales),
      darkIntents = generateDarkIntents(palette),
      lightIntents = generateLightIntents(palette),
      darkSemanticColors = generateDarkSemanticColors(palette),
      lightSemanticColors = generateLightSemanticColors(palette),
      lightLinkColors = generateLightLinkColors(palette),
      darkLinkColors = generateDarkLinkColors(palette),
      lightContentColors = generateLightContentColors(palette),
      darkContentColors = generateDarkContentColors(palette),
      lightBackgroundColors = generateLightBackgroundColors(palette),
      darkBackgroundColors = generateDarkBackgroundColors(palette);
  var modes = {
    light: __assign(__assign({
      mode: 'light'
    }, generateLightMode(scales, palette)), {
      palette: palette,
      scales: scales,
      intent: lightIntents,
      semantic: lightSemanticColors,
      link: lightLinkColors,
      content: lightContentColors,
      backgrounds: lightBackgroundColors
    }),
    dark: __assign(__assign({
      mode: 'dark'
    }, generateDarkMode(scales, palette)), {
      palette: palette,
      scales: scales,
      intent: darkIntents,
      semantic: darkSemanticColors,
      link: darkLinkColors,
      content: darkContentColors,
      backgrounds: darkBackgroundColors
    })
  };
  return {
    colors: modes.light,
    modes: modes
  };
}
export var ionColors = generateColorsFromScales(redesignColorScales);